import { ensureReferralCode, isShortReferralCode } from "./backend/utils/referral-code";
import {
  SHARER_DEVICE_LOCAL_STORAGE_KEY,
  getCurrentSharerDeviceCode,
  SHARER_DEVICE_QUERY_PARAMETER,
  setSharerDeviceParameter,
  sharerDeviceFromCode,
} from "./utils/sharer-device";
import { usePendingReferral } from "./backend/utils/usePendingReferral";
import { getStoredReferrerUserId } from "./backend/utils/referrer-id";
import React, { useEffect, useRef } from "react";
import { Navigate, Route, Routes, useNavigate, useLocation } from "react-router-dom";
const HomePage = React.lazy(() => import("./pages/home-page/HomePage"));
import { useTutorAvailability } from "./features/tutor/integration/tutorAvailability";
import { useTutorStudySuggestionsWarmup } from "./features/tutor/integration/tutorStudySuggestionsWarmup";
import { isEmailActionPath } from "./utils/authentication/email-action-path";

import {
  UserType,
  SignUpAndOnboardingModalView,
  CardType,
  EventSentToMobileApp,
  ManagerSubscriptionAccount,
  JUNGLE_BACKEND_PROD_DOMAIN,
  JUNGLE_CLOUDFLARE_BASE_URL,
  JUNGLE_SUPPORT_UI_IS_ENABLED,
  PAGE_HEADER_HEIGHT,
  PAGE_HEADER_HEIGHT_MOBILE,
  NUM_OF_ANSWER_FEEDBACKS_ALLOWED_PER_TIME_LIMIT,
  NUM_OF_CHAT_MESSAGES_ALLOWED_PER_TIME_LIMIT,
  NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED,
  SubscriptionStatus,
  APP_SETTINGS_COLLECTION_NAME,
  NewFeaturesForWhatsNewModal,
  UserSubscriptionPlan,
} from "./constants/GlobalTypesAndEnumsAndConstants";
import { getLocationData } from "./backend/utils/helper-methods";
import { EMAIL_LINK_CLICKED_EVENT, takePendingEmailLinkArrival } from "./utils/analytics/email-link-arrival";
import {
  answerServiceWorkerPushNotificationOpenedMessage,
  takePendingPushNotificationNewWindowArrival,
} from "./utils/analytics/push-notification-opens";
import {
  reportPushNotificationOpened,
  reportPushNotificationOpenedAndOpenItsScreen,
} from "./backend/utils/push-notification-opened";
import { isMobile } from "./backend/utils/detect-device";
import { useSearchParams } from "react-router-dom";
import {
  generateCardsStore,
  navigationWithStatesStore,
  pageStore,
  signUpAndOnboardingModalStore,
  trackingStore,
  useGeneratedCardsStore,
  useReviewDeckStore,
  useSubscriptionUpsellStore,
  userStore,
} from "./zustand/globalStore";
import LoadingViewComponent from "./components/LoadingViewComponent";
import PageWrapper from "./components/page-wrapper-component/PageWrapper";
import { useAuth } from "./contextsAndHooks/AuthContext";
import {
  encryptText,
  trackEvent,
  identifyUserForAnalytics,
  refreshDesktopPushNotificationTokenWithoutAsking,
} from "./backend/utils/helper-methods";
import { getUser, updateUser, checkAndUpdateUserTimezone } from "./backend/models/UserModel";
import { useHelperMethodsHook } from "./contextsAndHooks/HelperMethodsHook";

import { doc, onSnapshot } from "firebase/firestore";
import { db } from "./backend/utils/init-firebase";
import { getStripePriceDetails } from "./backend/utils/monetizationHelperMethods";
import { getListOfContentCreatorEmails } from "./backend/utils/firebaseMigrationsAndFieldChanges";
import { createAppVersionMonitor } from "./utils/app-version";
import { Flex, Text } from "@chakra-ui/react";
import Button from "./components/buttons/Button";
import { colors } from "./constants/GlobalStyles";
import { useTranslate } from "@tolgee/react";
import { isMetaAdVisit } from "./utils/acquisition-campaign-params";
import { buildAdminIndexRedirectTarget, isAdminIndexPath } from "./admin-pages/admin-navigation/adminIndexRedirect";
import { resumePendingReviewAfterAuthentication } from "./backend/utils/review-auth-gate";
import { markStartupAuthenticationReady } from "./utils/startup-profile";
import { loadModuleWithPreloadFallback } from "./utils/vite-preload-error-recovery";
// Always part of the main CSS bundle: the build-time static marketing pages rely on it before React boots.
import "./pages/marketing-site/marketingContent.css";
import { getAppRedirectUrlForNonMarketingPath, isMarketingSitePath } from "./pages/marketing-site/marketingSiteRoutes";
import { JungleColorThemeSync } from "./theme/JungleColorThemeSync";
import { AppLanguageSync } from "./i18n/AppLanguageSync";
import { AppLocaleSync } from "./i18n/AppLocaleSync";
import { MarketingSiteLoadingFallback } from "./pages/marketing-site/MarketingSiteLoadingFallback";
import { GenerateQuestionsPage, PreviewOfMiniLessonPage } from "./pages/uploadJourneyPages";
// Lazy: the landing page, blog and other jungleai.com pages stay out of the app's startup bundle.
const MarketingSiteRouter = React.lazy(() => import("./pages/marketing-site/MarketingSiteRouter"));
const LearningJourneyOverviewPage = React.lazy(() => import("./pages/review-deck-page/LearningJourneyOverviewPage"));
const SharedStudyGuidePage = React.lazy(() => import("./features/study-guides/SharedStudyGuidePage"));
const StudyGuidePage = React.lazy(() => import("./features/study-guides/StudyGuidePage"));
const LegacySummaryOutlineRedirect = React.lazy(() => import("./features/study-guides/StudyGuidePage").then(module => ({ default: module.LegacySummaryOutlineRedirect })));
const ClinicalExamStationPage = React.lazy(
  () => import("./pages/clinical-exam-practice-page/ClinicalExamStationPage")
);
const GiveFeedbackViewPage = React.lazy(() => import("./pages/GiveFeedbackViewPage"));
const DeckOverviewPage = React.lazy(() => import("./pages/deck-overview-page/DeckOverviewPage"));
const SettingsPage = React.lazy(() => import("./pages/settings-page/SettingsPage"));
const SetUpAnkiPage = React.lazy(() => import("./pages/SetUpAnkiPage"));
const SignUpWithEmailPage = React.lazy(() => import("./pages/SignUpWithEmailPage"));
const ReviewQuestionsPage = React.lazy(() => import("./pages/review-deck-page/review-questions-page/ReviewQuestionsPage"));
const AddCardPage = React.lazy(() => import("./pages/AddCardPage"));
const EditCardPage = React.lazy(() => import("./pages/EditCardPage"));
const OnboardingPage = React.lazy(() => import("./pages/onboarding/OnboardingPage"));
const CreateAndUpdateDeckPage = React.lazy(() => import("./pages/CreateAndUpdateDeckPage"));
const UpgradeSubscriptionPage = React.lazy(() => import("./pages/UpgradeSubscriptionPage"));
const GenerationHistoryPage = React.lazy(() => import("./pages/GenerationHistoryPage"));
const PaymentSuccessConfirmationPage = React.lazy(() => import("./pages/PaymentSuccessConfirmationPage"));
const SignInOrSignUpOptionsPage = React.lazy(() => import("./pages/SignInOrSignUpOptionsPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const EmailActionPage = React.lazy(() => import("./pages/EmailActionPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));
const ConceptPage = React.lazy(() => import("./pages/concept-page/ConceptPage"));
const GoogleCampaignsTrackingPage = React.lazy(() => import("./pages/GoogleCampaignsTrackingPage"));
const AuthByToken = React.lazy(() => import("./pages/AuthByToken"));
const ConceptsPage = React.lazy(() => import("./pages/concepts-page/ConceptsPage"));
const ConceptsLetterPage = React.lazy(() => import("./pages/concepts-page/ConceptsLetterPage"));
const LogoutPage = React.lazy(() => import("./pages/LogoutPage"));
const PauseRemindersPage = React.lazy(() => import("./pages/settings-page/PauseReviewRemindersPage"));
const StreakCalendarAndStatsPage = React.lazy(() => import("./pages/review-deck-page/StreakCalendarAndStatsPage"));
const UnsubscribePage = React.lazy(() => import("./pages/UnsubscribePage"));
const CreatorDashboardPage = React.lazy(() => import("./admin-pages/creator-dashboard/CreatorDashboardPage"));
const CreatorLeaderboardPage = React.lazy(() => import("./admin-pages/creator-leaderboard/CreatorLeaderboardPage"));
const CreatorsManagementPage = React.lazy(() => import("./admin-pages/creators-management/CreatorsManagementPage"));
const CreatorPayoutsPage = React.lazy(() => import("./admin-pages/creator-payouts/CreatorPayoutsPage"));
const LiveRecordingPage = React.lazy(() => import("./pages/live-recording/LiveRecordingPage"));
const VoiceConversationsListPage = React.lazy(() => import("./admin-pages/voice-conversations/VoiceConversationsListPage"));
const VoiceCallsPage = React.lazy(() => import("./admin-pages/voice-calls/VoiceCallsPage"));
const VoiceCallDetailPage = React.lazy(() => import("./admin-pages/voice-calls/VoiceCallDetailPage"));
const ProductionMonitorPage = React.lazy(() => import("./admin-pages/production-monitor/ProductionMonitorPage"));
const StudyGuideDashboardPage = React.lazy(() => import("./admin-pages/study-guides-dashboard/StudyGuideDashboardPage"));
const VoiceConversationDetailPage = React.lazy(() => import("./admin-pages/voice-conversations/VoiceConversationDetailPage"));
const VoiceFeedbackListPage = React.lazy(() => import("./admin-pages/voice-feedback/VoiceFeedbackListPage"));
const QualityPage = React.lazy(() => import("./pages/QualityPage"));
const StudyGuideEvalsPage = React.lazy(() => import("./admin-pages/evals/StudyGuideEvalsPage"));
const QuestionGenerationEvalsPage = React.lazy(() => import("./admin-pages/evals/QuestionGenerationEvalsPage"));
const TableQuestionsReviewPage = React.lazy(() => import("./admin-pages/evals/TableQuestionsReviewPage"));
const QualityBenchmarkEvalsPage = React.lazy(() => import("./admin-pages/evals/QualityBenchmarkEvalsPage"));
const CancelSubscriptionConfirmedPage = React.lazy(() => import("./pages/cancel-subscription-confirmed-page/CancelSubscriptionConfirmedPage"));
const MoveDeckToAnotherDeck = React.lazy(() => import("./pages/MoveDeckToAnotherDeck"));
const AdminSearchUsersPage = React.lazy(() => import("./admin-pages/user-admin-pages/AdminSearchUsersPage"));
// Local development only: the memory viewer for evaluation learners built outside the app (never in a build).
const TutorMemoryEvaluationHarness = import.meta.env.DEV ? React.lazy(() => import("./admin-pages/tutor-memory/TutorMemoryEvaluationHarness")) : null;
const AdminVerifyAppleSubscription = React.lazy(() => import("./admin-pages/user-admin-pages/AdminVerifyAppleSubscription"));
const AdminPageWrapper = React.lazy(() => import("./admin-pages/AdminPageWrapper"));
const AdminSettingsPage = React.lazy(() => import("./admin-pages/settings/AdminSettingsPage"));
const AdminVerifyGoogleSubscription = React.lazy(() => import("./admin-pages/user-admin-pages/AdminVerifyGoogleSubscription"));
const CloseTabPage = React.lazy(() => import("./pages/CloseTabPage"));
const GratitudeWallScreen = React.lazy(() => import("./pages/generate-questions-page/GratitudeWallScreen"));
const LeaderboardPage = React.lazy(() => import("./pages/leaderboard/LeaderboardPage"));
const BackpackWithBadgesAndTimeCapsulePage = React.lazy(() => import("./pages/backpack-page/BackpackWithBadgesAndTimeCapsulePage"));
const MermaidRendererCanaryPage = React.lazy(() => import("./pages/internal/MermaidRendererCanaryPage"));
const NotFoundPage = React.lazy(() => import("./pages/not-found-page/NotFoundPage"));
const loadTutorRoute = () => import("./features/tutor/integration/TutorRoute");
const TutorRoute = React.lazy(loadTutorRoute);
// Opening a Tutor link starts downloading its code while the account loads, instead of after. Code only:
// whether the Tutor shows is still decided by the availability gate below.
if (typeof window !== "undefined" && window.location.pathname.startsWith("/tutor")) void loadTutorRoute().catch(() => undefined);

const CustomerSupportChatPanel = React.lazy(
  () =>
    import(
      "./admin-pages/user-admin-pages/admin_support/threads_support/customer_chat_panel/CustomerSupportChatPanel"
    )
);
const AdminSupportThreadsPage = React.lazy(
  () => import("./admin-pages/user-admin-pages/admin_support/AdminSupportThreadsPage")
);
const AdminSupportKnowledgeBasePage = React.lazy(
  () => import("./admin-pages/user-admin-pages/admin_support/AdminSupportKnowledgeBasePage")
);
const AdminSupportLabelsPage = React.lazy(
  () => import("./admin-pages/user-admin-pages/admin_support/AdminSupportLabelsPage")
);
const AdminSupportChannelSettingsPage = React.lazy(
  () => import("./admin-pages/user-admin-pages/admin_support/AdminSupportChannelSettingsPage")
);
const AdminSupportInvestigationsPage = React.lazy(
  () =>
    import(
      "./admin-pages/user-admin-pages/admin_support/investigations/AdminSupportInvestigationsPage"
    )
);
const AdminSupportSystemChangesPage = React.lazy(
  () => import("./admin-pages/user-admin-pages/admin_support/AdminSupportSystemChangesPage")
);

function MainAppRouter() {
  const navigate = useNavigate();
  // Runtime, server-decided Tutor gate (tutorAvailability.ts). Everyone else keeps today's not-found.
  const tutorAvailability = useTutorAvailability();
  // Coming back to Jungle anywhere brings a few classes' suggested study topics up to date in the background.
  useTutorStudySuggestionsWarmup(tutorAvailability.available);
  const location = useLocation();
  const isTokenHandoffRoute = /^\/(?:auth-by-token|auth)\//.test(location.pathname);
  const appVersionMonitor = useRef<ReturnType<typeof createAppVersionMonitor> | null>(null);
  const { t } = useTranslate();
  const { authenticationLoading, accountSetupError, accountLoadError } = useAuth();
  const { sendEventsToMobileApp, snapshotStreakAndExamModelByUserId } = useHelperMethodsHook();
  // zustand stores
  const {
    setUserReferrerId,
    setDidUserComeFromMetaAd,
    setGoogleCampaignId,
    setTiktokCampaignId,
    setTiktokClickId,
    setRedditCampaignId,
    setInfluencerCampaignId,
    setNewsletterCampaignId,
    setIdOfAmbassadorThatReferredUser,
  } = trackingStore((state) => state);
  const { shouldShowModalSignUpAndLogin, setShowModalSignUpAndLogin, setHideModalSignUpAndLogin } =
    signUpAndOnboardingModalStore((state) => state);
  const {
    setisUserDoingDailyReview,
    setShouldBeSharedDeckDuplicated,
    pendingReviewDestinationAfterAuthentication,
  } = useReviewDeckStore((state) => state);
  const { isRunningAsMobileApp } = pageStore((state) => state);
  const { setDidShowNotificationOptInScreenForCurrentNumberOfReviewSessionsStarted } = navigationWithStatesStore(
    (state) => state
  );
  const { shouldShowEditOcclusionBoxesView, setShouldShowEditOcclusionBoxesView } = useGeneratedCardsStore(
    (state) => state
  );
  const { fileUploadErrorMessage } = generateCardsStore((state) => state);
  // Selectors for modal cleanup on route change (spec §10 edge case #9) — use selectors, not full destructure
  const hideSubscriptionUpsellModal = useSubscriptionUpsellStore((s) => s.hideSubscriptionUpsellModal);
  const closeQuestionQualityUpsellModal = useSubscriptionUpsellStore((s) => s.closeQuestionQualityUpsellModal);

  // redux states
  const user = userStore((state) => state.currentUser);
  usePendingReferral(user?.id, user?.pending_referral_code);

  // refs
  const [searchParams] = useSearchParams();
  const redirectLocation = searchParams.get("redirect");

  // variables
  const shouldShowOnboardingChooseStudentTypeView =
    user && (!user?.user_type || (user?.user_type === UserType.Student && !user?.student_type));

  useEffect(() => {
    if (authenticationLoading) return;
    markStartupAuthenticationReady(Boolean(user));
    if (isRunningAsMobileApp || window.ReactNativeWebView) {
      sendEventsToMobileApp(EventSentToMobileApp.LOADED_PAGE);
    }
    // This is the app-level native splash boundary. It intentionally depends
    // on authentication readiness rather than an individual route mounting.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticationLoading, isRunningAsMobileApp]);

  const shouldShowOnboardingAddNameView: boolean = user && !user.name && !user.has_user_visited_deck_overview_page;

  // set up tracking from different sources
  useEffect(() => {
    if (authenticationLoading) return;

    const utmSource = searchParams.get("utm_source");
    const googleCampaignIdFromParams = searchParams.get("gc_id");
    const tiktokCampaignIdFromParams = searchParams.get("ttc_id");
    const tiktokClickIdFromParams = searchParams.get("ttclid");
    const redditCampaignIdFromParams = searchParams.get("rc_id");
    const influencerCampaignIdFromParams = searchParams.get("ic_id");
    const newsletterCampaignIdFromParams = searchParams.get("nlc_id");
    const idOfAmbassadorThatReferredUserFromParams = searchParams.get("a");
    const encryptedReferrerUserIdFromParams = searchParams.get("r");
    const sharerDeviceCodeFromParams = searchParams.get(SHARER_DEVICE_QUERY_PARAMETER);
    const webSessionId = searchParams.get("ws");

    const didUserComeFromMetaAdLocalStorage = localStorage.getItem("did_user_come_from_meta_ad");
    const googleCampaignIdFromLocalStorage = localStorage.getItem("gc_id");
    const tiktokCampaignIdFromLocalStorage = localStorage.getItem("ttc_id");
    const tiktokClickIdFromLocalStorage = localStorage.getItem("ttclid");
    const redditCampaignIdFromLocalStorage = localStorage.getItem("rc_id");
    const influencerCampaignIdFromLocalStorage = localStorage.getItem("ic_id");
    const newsletterCampaignIdFromLocalStorage = localStorage.getItem("nlc_id");
    const idOfAmbassadorThatReferredUserFromLocalStorage = localStorage.getItem("a");
    const encryptedReferrerUserIdFromLocalStorage = localStorage.getItem("ref_uid");

    setUserReferrerId(null);

    function removeQueryParam(paramToRemove: string) {
      const parsedUrl = new URL(window.location.href);
      parsedUrl.searchParams.delete(paramToRemove);
      window.history.replaceState(window.history.state, document.title, parsedUrl.toString());
    }

    // Persist before resolving: signup can begin while the network lookup is pending.
    const referralCode = encryptedReferrerUserIdFromParams || encryptedReferrerUserIdFromLocalStorage;
    if (referralCode && (referralCode === "true" || isShortReferralCode(referralCode) || referralCode.length > 25)) {
      localStorage.setItem("ref_uid", referralCode);
      // The sharer's device travels with their referral code, so it is kept only when this address brought the code.
      if (encryptedReferrerUserIdFromParams) {
        // A new referrer replaces the remembered one's device, even when its link carried none.
        if (sharerDeviceFromCode(sharerDeviceCodeFromParams)) {
          localStorage.setItem(SHARER_DEVICE_LOCAL_STORAGE_KEY, sharerDeviceCodeFromParams as string);
        } else {
          localStorage.removeItem(SHARER_DEVICE_LOCAL_STORAGE_KEY);
        }
      }
      void getStoredReferrerUserId().then(referrerId => {
        if (localStorage.getItem("ref_uid") !== referralCode || !referrerId) return;
        // A visitor opening their own link is not referred by anyone, so their own device is not a sharer's.
        if (referrerId === user?.id) {
          localStorage.removeItem(SHARER_DEVICE_LOCAL_STORAGE_KEY);
          return;
        }
        setUserReferrerId(referrerId);
        if (encryptedReferrerUserIdFromParams && referrerId !== "true") {
          trackEvent("Referral: User came from a referral link", { referrerId, sharerDevice: sharerDeviceFromCode(sharerDeviceCodeFromParams) }, user);
          getUser(referrerId).then(referrer => {
            if (referrer) trackEvent("Referral: User referred another user to site", { userIdForUserThatWasReferred: user?.id }, referrer);
          }).catch(() => undefined);
        }
      }).catch(() => undefined);
    }
    if (encryptedReferrerUserIdFromParams) {
      removeQueryParam("r");
      removeQueryParam(SHARER_DEVICE_QUERY_PARAMETER);
    }

    if (googleCampaignIdFromParams) {
      setGoogleCampaignId(googleCampaignIdFromParams);
      localStorage.setItem("gc_id", googleCampaignIdFromParams);
    } else if (googleCampaignIdFromLocalStorage) {
      setGoogleCampaignId(googleCampaignIdFromLocalStorage);
    }
    // Contract C1: fb_ad_id, a Meta utm_source (facebook, instagram, ig, fb, meta, msg, an) or fbclid.
    if (isMetaAdVisit(searchParams)) {
      setDidUserComeFromMetaAd(true);
      localStorage.setItem("did_user_come_from_meta_ad", "true");
    } else if (utmSource && utmSource.includes("push_notification")) {
      localStorage.setItem("did_user_come_from_push_notification", utmSource);
    } else if (didUserComeFromMetaAdLocalStorage) {
      setDidUserComeFromMetaAd(true);
    }
    if (utmSource) {
      localStorage.setItem("utm_source", utmSource);
    }

    if (tiktokCampaignIdFromParams) {
      setTiktokCampaignId(tiktokCampaignIdFromParams);
      localStorage.setItem("ttc_id", tiktokCampaignIdFromParams);
    } else if (tiktokCampaignIdFromLocalStorage) {
      setTiktokCampaignId(tiktokCampaignIdFromLocalStorage);
    }
    if (tiktokClickIdFromParams) {
      setTiktokClickId(tiktokClickIdFromParams);
      localStorage.setItem("ttclid", tiktokClickIdFromParams);
    } else if (tiktokClickIdFromLocalStorage) {
      setTiktokClickId(tiktokClickIdFromLocalStorage);
    }
    if (redditCampaignIdFromParams) {
      setRedditCampaignId(redditCampaignIdFromParams);
      localStorage.setItem("rc_id", redditCampaignIdFromParams);
    } else if (redditCampaignIdFromLocalStorage) {
      setRedditCampaignId(redditCampaignIdFromLocalStorage);
    }
    if (influencerCampaignIdFromParams) {
      setInfluencerCampaignId(influencerCampaignIdFromParams);
      localStorage.setItem("ic_id", influencerCampaignIdFromParams);
    } else if (influencerCampaignIdFromLocalStorage) {
      setInfluencerCampaignId(influencerCampaignIdFromLocalStorage);
    }
    if (newsletterCampaignIdFromParams) {
      setNewsletterCampaignId(newsletterCampaignIdFromParams);
      localStorage.setItem("nlc_id", newsletterCampaignIdFromParams);
    } else if (newsletterCampaignIdFromLocalStorage) {
      setNewsletterCampaignId(newsletterCampaignIdFromLocalStorage);
    }
    if (idOfAmbassadorThatReferredUserFromParams) {
      setIdOfAmbassadorThatReferredUser(idOfAmbassadorThatReferredUserFromParams);
      const idOfAmbassadorThatReferredUserLoc = localStorage.getItem("a");
      localStorage.setItem("a", idOfAmbassadorThatReferredUserFromParams);

      // only track the event once
      if (!idOfAmbassadorThatReferredUserLoc) {
        trackEvent("Referral: Ambassador referred another user to site", {
          userIdForUserThatWasReferred: user?.id,
          ambassadorId: idOfAmbassadorThatReferredUserFromParams,
        });
      }
    } else if (idOfAmbassadorThatReferredUserFromLocalStorage) {
      setIdOfAmbassadorThatReferredUser(idOfAmbassadorThatReferredUserFromLocalStorage);
    }
    if (webSessionId) {
      localStorage.setItem("web_session_id", webSessionId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [authenticationLoading]);

  useEffect(() => {
    // add or remove the duplicate deck param used for idenfitying if a deck should be duplicated
    const shouldBeDuplicateDeck = searchParams.get("duplicate") === "true";
    setShouldBeSharedDeckDuplicated(shouldBeDuplicateDeck);
    const urlObj = new URL(window.location.href);
    urlObj.searchParams.delete("duplicate");
    window.history.replaceState(window.history.state, "", urlObj.toString());

    const monitor = createAppVersionMonitor({
      storage: {
        getItem: (key) => localStorage.getItem(key),
        setItem: (key, value) => localStorage.setItem(key, value),
      },
      getPathname: () => window.location.pathname,
      reloadPage: () => window.location.reload(),
    });
    appVersionMonitor.current = monitor;
    // Snapshots queue updates; navigation below applies them without
    // interrupting work embedded in overview pages (notes, recordings, uploads).
    const appSettingsDocRef = doc(db, APP_SETTINGS_COLLECTION_NAME, "app");
    return onSnapshot(appSettingsDocRef, (appSettingsSnapshot) => {
      if (appSettingsSnapshot.exists()) {
        monitor.receiveVersion(appSettingsSnapshot.data().version);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, []);

  useEffect(() => {
    appVersionMonitor.current?.reloadIfReady();
  }, [location.pathname]);

  useEffect(() => {
    if (user?.subscription_information?.manager_subscription_account === ManagerSubscriptionAccount.Apple) {
      //verify apple subscription status
      fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/check_apple_subscription`, {
        method: "POST",
        body: JSON.stringify({
          user_id: user?.id,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
    if (user?.subscription_information?.manager_subscription_account === ManagerSubscriptionAccount.Google) {
      //verify google subscription status
      fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/users/check_google_play_subscription`, {
        method: "POST",
        body: JSON.stringify({
          user_id: user?.id,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
    }
    checkAndUpdateUserTimezone(user);
    if (localStorage.getItem("did_user_come_from_push_notification")) {
      trackEvent(
        "Desktop Notification opened",
        {
          push_notification_type: localStorage.getItem("did_user_come_from_push_notification"),
        },
        user
      );
      localStorage.removeItem("did_user_come_from_push_notification");
    }

    const doesUserHaveDesktopReviewRemindersEnabled =
      user?.user_settings?.notification_preferences?.review_reminder_on_desktop;
    const doesUserHaveADesktopToken = user?.user_settings?.desktop_push_notification_token;

    // Only when this browser already allows notifications: the app never opens the browser's prompt on its own.
    if (doesUserHaveDesktopReviewRemindersEnabled && !doesUserHaveADesktopToken) {
      refreshDesktopPushNotificationTokenWithoutAsking(
        user,
        "on home page - review reminders are enabled but there is no token"
      );
    }
    let snapshotStreakAndExamModelByUserIdFunc = snapshotStreakAndExamModelByUserId();
    return () => {
      snapshotStreakAndExamModelByUserIdFunc && snapshotStreakAndExamModelByUserIdFunc();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [user?.id]);

  useEffect(() => {
    if (!user?.exams_properties?.exam_ids) return;

    if (
      isRunningAsMobileApp &&
      user?.features_user_has_seen_in_whats_new_list?.includes(NewFeaturesForWhatsNewModal.WidgetSetup) &&
      user?.user_settings?.has_widget_been_configured
    ) {
      sendEventsToMobileApp(EventSentToMobileApp.RELOAD_WIDGET_DATA);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [user?.exams_properties?.exam_ids]);

  useEffect(() => {
    // get stripe price details ahead of time so it immediately loads if they open the upsell modal
    const fetchPricesWithCurrentLocation = async () => {
      if (user) {
        // Get current location data
        const locationData = await getLocationData();
        const currentCountry = locationData.country || user?.country_code || "US";

        // Store the current country in the global store
        pageStore.getState().setCurrentCountryUserIsIn(currentCountry);

        // Create a user object with the current country for pricing
        const userWithCurrentCountry = {
          ...user,
          country_code: currentCountry,
        };

        getStripePriceDetails(userWithCurrentCountry);

        // Request subscription prices from mobile app if running on mobile
        // mobile will respond with prices in theSEND_SUBSCRIPTION_PRICES_TO_WEB event
        if (isRunningAsMobileApp || window.ReactNativeWebView) {
          sendEventsToMobileApp(EventSentToMobileApp.REQUEST_SUBSCRIPTION_PRICES_FROM_MOBILE_APP);
        }
      }
    };

    fetchPricesWithCurrentLocation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [user?.id]);

  // Check for content creators and @tec.mx users
  useEffect(() => {
    const checkSpecialUserAccess = async () => {
      if (!user || !user.email_auth) return;

      // Check if user is from tec.mx and remove their access if criteria met
      if (
        user.email_auth.toLowerCase().includes("@tec.mx") &&
        user.subscription_information?.subscription_status === SubscriptionStatus.ACTIVE &&
        !user.subscription_information?.last_time_subscription_renewed_date &&
        !user.subscription_information?.first_time_subscription_activated_date
      ) {
        await updateUser(user.id, {
          subscription_information: {
            ...user.subscription_information,
            subscription_status: null,
            subscription_plan_type: UserSubscriptionPlan.FREE,
          },
        });
      }

      // Check if user is a content creator and grant Super Learner access
      const creatorCheck = await getListOfContentCreatorEmails(user.email_auth, user.id, user);
      if (
        creatorCheck.isCreator &&
        (user.subscription_information?.subscription_status !== SubscriptionStatus.ACTIVE ||
          // Elite counts too — never DOWNGRADE an Elite creator to Super Learner.
          (user.subscription_information?.subscription_plan_type !== UserSubscriptionPlan.SUPER_LEARNER &&
            user.subscription_information?.subscription_plan_type !== UserSubscriptionPlan.ELITE))
      ) {
        await updateUser(user.id, {
          subscription_information: {
            ...user.subscription_information,
            subscription_plan_type: UserSubscriptionPlan.SUPER_LEARNER,
            subscription_status: SubscriptionStatus.ACTIVE,
          },
        });
      }
    };

    checkSpecialUserAccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [user?.email_auth]);

  useEffect(() => {
    if (location.pathname.includes("review-decks")) {
      setisUserDoingDailyReview(true);
    } else if (!location.pathname.includes("give-feedback")) {
      setisUserDoingDailyReview(false);
    }

    if (!location.pathname.includes("review-deck") && shouldShowEditOcclusionBoxesView) {
      setShouldShowEditOcclusionBoxesView(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [location.pathname]);

  // Dismiss upsell modals on route change so stale modal state can't leak across pages (spec §10 edge case #9).
  // The two store actions are stable Zustand refs (listed in deps to satisfy exhaustive-deps; they never
  // change identity, so this effect still only re-runs on a pathname change).
  useEffect(() => {
    hideSubscriptionUpsellModal();
    closeQuestionQualityUpsellModal();
  }, [location.pathname, hideSubscriptionUpsellModal, closeQuestionQualityUpsellModal]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    void ensureReferralCode(user.id).then(code => {
      if (!code || cancelled || isAdminIndexPath(window.location.pathname)) return;
      const address = new URL(window.location.href);
      address.searchParams.set("r", code);
      setSharerDeviceParameter(address.searchParams);
      window.history.replaceState(window.history.state, "", address.pathname + address.search + address.hash);
    }).catch(() => undefined);
    return () => { cancelled = true; };
  }, [user?.id]);

  // The address bar is the link people copy, so it must always carry the sharer (`r=`) and device (`s=`) params.
  // The effect below sets them on a page change; this one puts back what an in-app navigation to the same page
  // dropped (for example a tab or filter that replaces the whole query string). It only adds what is missing and
  // never replaces an existing `r`, so it does not fight the referral-code effect above.
  useEffect(() => {
    if (!user?.id || isAdminIndexPath(location.pathname)) return;
    const liveSearchParams = new URLSearchParams(window.location.search);
    if (liveSearchParams.has("r") && liveSearchParams.get(SHARER_DEVICE_QUERY_PARAMETER) === getCurrentSharerDeviceCode()) return;
    if (!liveSearchParams.has("r")) liveSearchParams.set("r", `${encryptText(user.id)}`);
    setSharerDeviceParameter(liveSearchParams);
    navigate(`${location.pathname}?${liveSearchParams.toString()}${window.location.hash}`, { replace: true, state: location.state });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Re-check on every address change; navigate is stable enough here.
  }, [location.pathname, location.search, user?.id]);

  useEffect(() => {
    // Let the admin index redirect own this navigation. Replacing /admin here
    // can race its child Navigate and strand a client-side visit on an empty route.
    if (!user?.id || isAdminIndexPath(location.pathname)) return;

    // add the user's referrer id to each url so if they share the url with someone else, we can track the referral
    // Read the live URL: a child page's effect can remove a one-shot parameter
    // (for example `autoGenerate`) in this same commit, and rebuilding from the
    // render-time location would put it back and replay that action on Back.
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("r", `${encryptText(user?.id)}`);
    // The device this address is shared from, so a visit made from the copied link can be split by it.
    setSharerDeviceParameter(searchParams);

    const newUrl = `${location.pathname}?${searchParams.toString()}${window.location.hash}`;
    navigate(newUrl, { replace: true, state: window.history.state?.usr ?? null });

    // intentionally checking for both review-deck and review-decks
    if (user?.id && location.pathname.startsWith("/review-deck")) {
      setDidShowNotificationOptInScreenForCurrentNumberOfReviewSessionsStarted(false);
      updateUser(user?.id, {
        number_of_review_session_started: (user?.number_of_review_session_started ?? 0) + 1,
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [location.pathname, user?.id]);

  useEffect(() => {
    // The lazy token page must consume its token and send partner verification
    // before it follows the redirect. Parent onboarding can otherwise unmount it.
    if (authenticationLoading || isTokenHandoffRoute) return;

    const handleOnboarding = async () => {
      if (!user) return;

      // If user needs onboarding (student type or name)
      if (shouldShowOnboardingChooseStudentTypeView || shouldShowOnboardingAddNameView) {
        // Optionally refresh user data (not used here, but could be for future logic)
        // const updatedUserData = await getUser(user.id);

        // If redirect is to upgrade, go to onboarding with redirect param
        if (redirectLocation && redirectLocation.includes("upgrade")) {
          navigate(`/onboarding?redirect=${redirectLocation}`);
          return;
        }

        // On the login and sign up pages (/login, /signup, /signup-email), open the first onboarding step and
        // go home with the search params. The step opens before the navigation, from what the app already
        // knows about the account (all of it, for one just created), so home's own name dialog never flashes
        // first (the dialog's code is fetched while these pages show; see the effect further down). And this
        // does not stop here: the effect follows the user's fields, not the location, so it would not run
        // again on home. Google, Apple and Outlook sign-ups on these pages (the landing page's "Sign Up" and
        // "Login" lead there) used to skip the student type step, and email sign-ups met it only after the
        // name step. getUser below re-checks which step is due.
        if (window.location.pathname.includes("/login") || window.location.pathname.includes("/signup")) {
          setShowModalSignUpAndLogin(
            shouldShowOnboardingChooseStudentTypeView
              ? SignUpAndOnboardingModalView.OnboardingStudentTypeView
              : SignUpAndOnboardingModalView.OnboardingAddNameView
          );
          navigate(`/${location.search}`, { replace: true });
        }

        // we're getting some sort of race condition with user state being outdated, so we're getting the user data again
        const updatedUserData = await getUser(user.id);
        const updatedShouldShowOnboardingChooseStudentTypeView =
          !updatedUserData?.user_type ||
          (updatedUserData?.user_type === UserType.Student && !updatedUserData?.student_type);

        // Show appropriate onboarding modal
        if (updatedShouldShowOnboardingChooseStudentTypeView) {
          setShowModalSignUpAndLogin(SignUpAndOnboardingModalView.OnboardingStudentTypeView);
        } else if (shouldShowOnboardingAddNameView) {
          setShowModalSignUpAndLogin(SignUpAndOnboardingModalView.OnboardingAddNameView);
        }
        return;
      }

      // If not onboarding, handle redirect or modal cleanup
      if (pendingReviewDestinationAfterAuthentication) {
        resumePendingReviewAfterAuthentication({ user, navigate });
      } else if (redirectLocation) {
        let formattedRedirectLocation = redirectLocation;
        if (formattedRedirectLocation[0] !== "/") {
          formattedRedirectLocation = `/${formattedRedirectLocation}`;
        }
        // The onboarding step that just finished is still open: close it, or it stays, empty, over the page
        // the learner is sent to (read from the store, as this effect's copy of the flag may be stale)
        if (signUpAndOnboardingModalStore.getState().shouldShowModalSignUpAndLogin) {
          setHideModalSignUpAndLogin();
        }
        navigate(formattedRedirectLocation);
      } else if (shouldShowModalSignUpAndLogin) {
        setHideModalSignUpAndLogin();
      } else if (window.location.pathname.includes("/login") || window.location.pathname.includes("/signup")) {
        navigate(`/${location.search}`, { replace: true });
      }
    };

    handleOnboarding();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [
    authenticationLoading,
    isTokenHandoffRoute,
    user?.user_type,
    user?.student_type,
    user?.university,
    shouldShowOnboardingAddNameView,
    shouldShowOnboardingChooseStudentTypeView,
    pendingReviewDestinationAfterAuthentication,
    user?.user_settings?.days_to_receive_a_review_reminder_on,
  ]);

  useEffect(() => {
    if (isRunningAsMobileApp || window.ReactNativeWebView) {
      sendEventsToMobileApp(EventSentToMobileApp.USER_DATA, JSON.stringify(user));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [user]);

  useEffect(() => {
    if (!user?.id) return;
    if (user) {
      const startSession = async () => {
        await updateUser(user.id, {
          date_of_last_visit: new Date(),
        }).catch((error) => {
          console.error("Error updating user date of last visit:", error);
        });

        // we'll schedule the next review reminder if
        // 1) the date_of_next_review_reminder is not set (new user)
        // 2) the date_of_next_review_reminder is in the past (user hasn't been on jungle for >7 days)
        fetch(`${JUNGLE_BACKEND_PROD_DOMAIN}/notifications/schedule_the_next_review_reminder_to_be_sent`, {
          method: "POST",
          body: JSON.stringify({
            user_id: user?.id,
          }),
          headers: {
            "Content-Type": "application/json",
          },
        }).catch((error) => {
          console.error("Error scheduling next review reminder:", error);
        });
      };
      startSession();
    }

    // set the default number of num_of_answer_feedbacks_remaining to 10 for users that don't have this field yet
    // this is a one time field value set for new fields that were added when we changed our pricing model - this can probably be deleted in 2025
    // Both writes carry only their own fields (updateUser merges maps): spreading this render's whole
    // subscription_information wrote its old values back, such as the free generations AuthContext had just
    // lowered for a new account in the one-generation countries.
    if (user && user?.subscription_information?.num_of_answer_feedbacks_remaining === undefined) {
      updateUser(user?.id, {
        subscription_information: {
          num_of_answer_feedbacks_remaining: NUM_OF_ANSWER_FEEDBACKS_ALLOWED_PER_TIME_LIMIT,
          num_of_chat_messages_remaining: NUM_OF_CHAT_MESSAGES_ALLOWED_PER_TIME_LIMIT,
          num_of_advanced_question_generations_left: NUM_OF_FREE_ADVANCED_QUESTION_GENERATIONS_ALLOWED,
          num_of_monthly_generations_left: (user?.subscription_information?.num_of_monthly_generations_left || 0) + 10,
        },
      });
    }

    // we need to set the num_of_answer_feedbacks_remaining to 5 for all the users due to the new feedback experiment
    if (user && !user?.subscription_information?.have_updated_num_of_answer_feedbacks_remaining) {
      updateUser(user?.id, {
        subscription_information: {
          num_of_answer_feedbacks_remaining: NUM_OF_ANSWER_FEEDBACKS_ALLOWED_PER_TIME_LIMIT,
          have_updated_num_of_answer_feedbacks_remaining: true,
        },
      });
    }
    if (user?.date_created) {
      identifyUserForAnalytics(user);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [user?.id]);

  // Report an arrival from an email link once per page load, after auth has settled: a signed-in visitor's
  // click is reported after they are identified, so it lands on their profile; a signed-out visitor's stays
  // anonymous.
  useEffect(() => {
    if (authenticationLoading) return;
    const emailLinkArrival = takePendingEmailLinkArrival();
    if (!emailLinkArrival) return;
    const identification = user?.date_created ? identifyUserForAnalytics(user) : Promise.resolve();
    void identification
      .catch(() => {})
      .then(() => trackEvent(EMAIL_LINK_CLICKED_EVENT, emailLinkArrival, user ?? null, null));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Runs when auth settles; the arrival is handed over once.
  }, [authenticationLoading, user?.id]);

  // A window the desktop push service worker opened for a tap is reported the same way, once per page load.
  // The address it opened is already the push's screen.
  useEffect(() => {
    if (authenticationLoading) return;
    const pushNotificationNewWindowArrival = takePendingPushNotificationNewWindowArrival();
    if (!pushNotificationNewWindowArrival) return;
    void reportPushNotificationOpened(pushNotificationNewWindowArrival, user ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Runs when auth settles; the arrival is handed over once.
  }, [authenticationLoading, user?.id]);

  // A tap on a desktop push while a Jungle window is open: the service worker tells the open windows which push was
  // tapped, has it opened in the most recently used one that answers and focuses that window
  // (public/firebase-messaging-sw.js).
  useEffect(() => {
    if (!("serviceWorker" in navigator) || !navigator.serviceWorker) return undefined;
    const handleMessageFromServiceWorker = (messageEvent: MessageEvent) =>
      answerServiceWorkerPushNotificationOpenedMessage(messageEvent, (openedPushNotification) => {
        void reportPushNotificationOpenedAndOpenItsScreen(openedPushNotification, navigate);
      });
    navigator.serviceWorker.addEventListener("message", handleMessageFromServiceWorker);
    // Messages wait in a queue until the page says it listens; this listener is the page's only one.
    navigator.serviceWorker.startMessages?.();
    return () => navigator.serviceWorker.removeEventListener("message", handleMessageFromServiceWorker);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- navigate is the router's and stays the same.
  }, []);

  // Signing in on the login or sign up page opens the onboarding dialog the moment the account is ready. Its
  // code is loaded only when a dialog first opens, so fetch it while these pages show: otherwise home is on
  // screen, and can be tapped, for the half second that takes on a phone. A failed fetch is left to the
  // dialog's own load: reloading the page here could wipe a sign up form mid-typing.
  useEffect(() => {
    if (location.pathname.startsWith("/login") || location.pathname.startsWith("/signup")) {
      void loadModuleWithPreloadFallback(
        () => import("./components/page-wrapper-component/ModalSignUpLoginAndOnboardingComponent"),
        () => null
      );
    }
  }, [location.pathname]);

  useEffect(() => {
    // The first-time home shows an upload's error under the upload that failed; any other upload's error opens the
    // upload page
    if (fileUploadErrorMessage && !generateCardsStore.getState().isFirstTimeHomeOnScreen) {
      navigate("/upload-document");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- Preserve the existing lifecycle; refactor with behavior-specific tests.
  }, [fileUploadErrorMessage]);

  // Mount partner auth routes BEFORE the authenticationLoading gate.
  // AuthByToken calls signInWithCustomToken then redirects once currentUser
  // populates. Gating it behind authenticationLoading causes a permanent
  // splash screen for returning users whose Firestore onSnapshot fires
  // hasPendingWrites before the user doc is resolved.
  // Wrapped in Routes/Route so useParams() in AuthByToken receives tokenSession.
  if (location.pathname.startsWith("/auth-by-token/")) {
    return (
      <React.Suspense fallback={<LoadingViewComponent />}>
        <Routes>
          <Route path="/auth-by-token/:tokenSession" element={<AuthByToken />} />
        </Routes>
      </React.Suspense>
    );
  }

  if (accountSetupError || accountLoadError) {
    return (
      <Flex
        minHeight="100vh"
        padding="24px"
        backgroundColor={colors.surface.offWhite}
        alignItems="center"
        justifyContent="center"
      >
        <Flex width="100%" maxWidth="480px" direction="column" alignItems="center" textAlign="center" gap="16px">
          <Text color={colors.text.header} fontSize={{ base: "24px", md: "28px" }} fontWeight={600} lineHeight="1.25">
            {accountLoadError
              ? t("account_load_error_title", "Jungle is temporarily unavailable")
              : t("account_setup_error_title", "we couldn’t finish setting up your account")}
          </Text>
          <Text color={colors.text.body} fontSize="16px" lineHeight="24px">
            {accountLoadError
              ? t("account_load_error_body", "We couldn’t load your account. Please try again shortly.")
              : t("account_setup_error_body", "your sign-in is safe. check your connection and try again.")}
          </Text>
          <Button title={t("try_again", "try again")} size="medium" onClick={() => window.location.reload()} />
        </Flex>
      </Flex>
    );
  }

  // The signed-in profile may still be loading, but a terminal error above takes priority.
  if (authenticationLoading) {
    return <LoadingViewComponent />;
  }

  return (
    <>
      {user && JUNGLE_SUPPORT_UI_IS_ENABLED ? (
        <React.Suspense fallback={null}>
          <CustomerSupportChatPanel />
        </React.Suspense>
      ) : null}
      <React.Suspense fallback={<LoadingViewComponent />}>
      <Routes>
      <Route path="/signup" element={<SignInOrSignUpOptionsPage />} />
      <Route path="/signup-email" element={<SignUpWithEmailPage />} />
      <Route path="/study-guide/:token/:descriptiveUrlSlug?" element={<React.Suspense fallback={<LoadingViewComponent />}><SharedStudyGuidePage /></React.Suspense>} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/quality" element={<QualityPage />} />
      {/* ROUTES FOR ADMIN USERS */}
      <Route
        path="admin"
        element={
          <Navigate
            to={buildAdminIndexRedirectTarget(location.search, JUNGLE_SUPPORT_UI_IS_ENABLED)}
            replace
          />
        }
      />
      <Route
        path="admin/*"
        element={
          <>
            {
              <AdminPageWrapper>
                <React.Suspense
                  fallback={
                    <LoadingViewComponent
                      minimumHeight={{
                        base: `calc(100dvh - ${isMobile ? PAGE_HEADER_HEIGHT_MOBILE : PAGE_HEADER_HEIGHT} - 76px)`,
                        md: `calc(100dvh - ${isMobile ? PAGE_HEADER_HEIGHT_MOBILE : PAGE_HEADER_HEIGHT} - 32px)`,
                      }}
                    />
                  }
                >
                  <Routes>
                    <Route path="/users" element={<AdminSearchUsersPage />} />
                    {TutorMemoryEvaluationHarness && <Route path="/tutor-memory-evaluation" element={<TutorMemoryEvaluationHarness />} />}
                    <Route path="/production-monitor" element={<ProductionMonitorPage />} />
                    <Route path="/study-guides" element={<StudyGuideDashboardPage />} />
                    <Route path="/verifyapple" element={<AdminVerifyAppleSubscription />} />
                    <Route path="/verifygoogle" element={<AdminVerifyGoogleSubscription />} />
                    <Route path="/creators" element={<CreatorsManagementPage />} />
                    <Route path="/creator-dashboard" element={<CreatorDashboardPage />} />
                    <Route path="/creator-leaderboard" element={<CreatorLeaderboardPage />} />
                    <Route path="/creator-payouts" element={<CreatorPayoutsPage />} />
                    <Route path="/evals/question-generation" element={<QuestionGenerationEvalsPage />} />
                    <Route path="/evals/study-guides" element={<StudyGuideEvalsPage />} />
                    <Route path="/evals/table-questions" element={<TableQuestionsReviewPage />} />
                    <Route path="/evals/quality" element={<QualityBenchmarkEvalsPage />} />
                    <Route path="/settings" element={<AdminSettingsPage />} />
                    <Route path="/voice-conversations" element={<VoiceConversationsListPage />} />
                    <Route path="/voice-calls" element={<VoiceCallsPage />} />
                    <Route path="/voice-calls/:kind/:callId" element={<VoiceCallDetailPage />} />
                    <Route path="/voice-conversations/:conversationId" element={<VoiceConversationDetailPage />} />
                    <Route path="/voice-feedback" element={<VoiceFeedbackListPage />} />
                    {JUNGLE_SUPPORT_UI_IS_ENABLED ? (
                      <>
                        <Route path="/support/threads" element={<AdminSupportThreadsPage />} />
                        <Route path="/support/investigations" element={<AdminSupportInvestigationsPage />} />
                        <Route path="/support/knowledge-base" element={<AdminSupportKnowledgeBasePage />} />
                        <Route path="/support/labels" element={<AdminSupportLabelsPage />} />
                        <Route path="/support/channel-settings" element={<AdminSupportChannelSettingsPage />} />
                        <Route path="/support/system-changes" element={<AdminSupportSystemChangesPage />} />
                      </>
                    ) : (
                      <Route path="/support/*" element={<Navigate to="/admin/users" replace />} />
                    )}
                  </Routes>
                </React.Suspense>
              </AdminPageWrapper>
            }
          </>
        }
      />
      {/* ROUTES FOR ALL USERS */}
      <Route
        path="/*"
        element={
          <>
            {
              <PageWrapper>
                <React.Suspense fallback={<LoadingViewComponent />}>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/upload-document" element={<HomePage shouldShowUploadQuestionsView={true} />} />
                  <Route path="/my-classes" element={<HomePage showClassesView={true} />} />
                  <Route path="/tutor/*" element={tutorAvailability.available ? <TutorRoute /> : tutorAvailability.resolved ? <NotFoundPage /> : <LoadingViewComponent />} />
                  <Route path="/leaderboard" element={<LeaderboardPage />} />
                  <Route path="/generate-questions" element={<GenerateQuestionsPage />} />
                  <Route path="/clinical-exam-practice/:clinicalExamId" element={<ClinicalExamStationPage />} />
                  <Route path="/generated-cards/:deckId/:indexOfCardToShow?" element={<ReviewQuestionsPage />} />
                  <Route path="/learning-journey-overview/:deckId" element={<LearningJourneyOverviewPage />} />
                  <Route path="/recording" element={<LiveRecordingPage />} />
                  <Route path="/recording/:id" element={<LiveRecordingPage />} />
                  {/* can remove this after nov 1st */}
                  <Route
                    path="/learning-journey/all-cards/:deckId/:indexOfCardToShow?"
                    element={<ReviewQuestionsPage />}
                  />
                  {/* MINI LESSON ROUTES */}
                  <Route path="/learning-journey/mini-lesson-preview/:deckId" element={<PreviewOfMiniLessonPage />} />
                  {/* <Route path="/learning-journey/free-recall/:deckId" element={<FreeRecallPage />} /> */}
                  {/* The /deck path is deprecated and will be removed in a future update. Please update all routes to use
                  /folder instead. */}
                  <Route path="/deck-overview/:deckId" element={<DeckOverviewPage />} />
                  <Route path="/deck-overview/:deckId/:descriptiveUrlSlug" element={<DeckOverviewPage />} />
                  <Route path="/deck/:deckId" element={<DeckOverviewPage />} />
                  <Route path="/deck/:deckId/:descriptiveUrlSlug" element={<DeckOverviewPage />} />
                  {/* END OF DEPRECATED ROUTES */}
                  <Route path="/folder-overview/:deckId" element={<DeckOverviewPage />} />
                  <Route path="/folder-overview/:deckId/:descriptiveUrlSlug" element={<DeckOverviewPage />} />
                  <Route path="/folder/:deckId" element={<DeckOverviewPage />} />
                  <Route path="/folder/:deckId/:descriptiveUrlSlug" element={<DeckOverviewPage />} />
                  <Route path="/move-deck/:deckId" element={<MoveDeckToAnotherDeck />} />
                  <Route path="/create-deck/:deckId?" element={<CreateAndUpdateDeckPage />} />
                  <Route path="/edit-deck" element={<CreateAndUpdateDeckPage />} />
                  <Route path="/review-deck/:deckId/:indexOfCardToShow?" element={<ReviewQuestionsPage />} />
                  <Route path="/card/:cardIdFromParam" element={<ReviewQuestionsPage />} />
                  <Route path="/study-guide" element={<StudyGuidePage />} />
                  <Route path="/summary-outline/:deckId" element={<LegacySummaryOutlineRedirect />} />
                  <Route
                    path="/review-deck/multiple-choice-questions/:deckId/:indexOfCardToShow?"
                    element={
                      <ReviewQuestionsPage
                        cardTypes={[
                          CardType.RegularMultipleChoiceQuestion,
                          CardType.CaseScenarioMultipleChoiceQuestion,
                        ]}
                      />
                    }
                  />
                  <Route
                    path="/review-deck/true-false-questions/:deckId/:indexOfCardToShow?"
                    element={<ReviewQuestionsPage cardTypes={[CardType.TrueOrFalseQuestion]} />}
                  />
                  <Route
                    path="/review-deck/free-response-questions/:deckId/:indexOfCardToShow?"
                    element={
                      <ReviewQuestionsPage
                        cardTypes={[CardType.RegularFreeResponseQuestion, CardType.CaseScenarioFreeResponseQuestion]}
                      />
                    }
                  />
                  <Route
                    path="/review-deck/case-scenario-questions/:deckId/:indexOfCardToShow?"
                    element={<ReviewQuestionsPage cardTypes={[CardType.CaseScenarioFreeResponseQuestion]} />}
                  />
                  <Route
                    path="/review-deck/image-occlusion-cards/:deckId/:indexOfCardToShow?"
                    element={<ReviewQuestionsPage cardTypes={[CardType.ImageOcclusion]} />}
                  />
                  <Route path="/review-decks" element={<ReviewQuestionsPage />} />
                  <Route path="/add-card/:deckId" element={<AddCardPage />} />
                  <Route path="/edit-card" element={<EditCardPage />} />
                  <Route path="/onboarding" element={<OnboardingPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/cancel-confirmed" element={<CancelSubscriptionConfirmedPage />} />
                  <Route path="/pause-reminders" element={<PauseRemindersPage />} />
                  <Route path="/give-feedback" element={<GiveFeedbackViewPage />} />
                  <Route path="/set-up-anki" element={<SetUpAnkiPage />} />
                  <Route path="/upgrade" element={<UpgradeSubscriptionPage />} />
                  <Route path="/close-tab" element={<CloseTabPage />} />
                  <Route path="/google_campaign_tracking" element={<GoogleCampaignsTrackingPage />} />
                  <Route path="/auth/:tokenSession" element={<AuthByToken />} />
                  {/* Alias for the URL emitted by jungle-backend's
                      /partners/signin-link, which builds
                      `${JUNGLE_WEB_BASE_URL}/auth-by-token/<token>?verify=1`.
                      Both paths must mount AuthByToken so signInWithCustomToken
                      fires; otherwise the partner-handoff lands on a route
                      that does not exist and the page hangs on the splash. */}
                  <Route path="/subscription-successful" element={<PaymentSuccessConfirmationPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/updates-unsubscribe" element={<UnsubscribePage />} />{" "}
                  {/* this is for unsubscribing from Jungle investor updates */}
                  <Route path="/concept/:conceptName" element={<ConceptPage />} />
                  <Route path="/generation-history" element={<GenerationHistoryPage />} />
                  <Route path="/concepts" element={<ConceptsPage />} />
                  <Route path="/gratitude-wall" element={<GratitudeWallScreen />} />
                  <Route path="/concepts/:letter/:page?" element={<ConceptsLetterPage />} />
                  <Route path="/logout" element={<LogoutPage />} />
                  <Route path="/streak-and-stats" element={<StreakCalendarAndStatsPage />} />
                  <Route path="/backpack" element={<BackpackWithBadgesAndTimeCapsulePage />} />
                  <Route
                    path="/backpack/badges"
                    element={<BackpackWithBadgesAndTimeCapsulePage shouldShowBadgesComponent={true} />}
                  />
                  {/* An address no route above matches (a mistyped or expired link). `/internal/*` addresses
                      are tools, not pages of the product: they keep rendering nothing here. Marketing,
                      sign-in and admin addresses never reach this list. */}
                  <Route path="/internal/*" element={<></>} />
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
                </React.Suspense>
              </PageWrapper>
            }
          </>
        }
      />
      </Routes>
      </React.Suspense>
    </>
  );
}

function AppRouterContent() {
  const location = useLocation();

  if (location.pathname === "/internal/mermaid-renderer-canary") {
    return <MermaidRendererCanaryPage />;
  }

  // Firebase email links must work while signed out and must not be redirected
  // by the main app's authentication and onboarding effects.
  if (isEmailActionPath(location.pathname)) {
    return (
      <Routes>
        <Route path="/auth/action/*" element={<EmailActionPage />} />
      </Routes>
    );
  }

  // jungleai.com pages (landing page, blog, pricing, …) are public and independent of the app's
  // authentication and onboarding effects, so they render outside the main app router.
  if (isMarketingSitePath(location.pathname)) {
    return (
      <React.Suspense fallback={<MarketingSiteLoadingFallback />}>
        <MarketingSiteRouter />
      </React.Suspense>
    );
  }

  // Any other path opened on the marketing domain belongs to the app, which only runs on its own origin.
  const appRedirectUrl = getAppRedirectUrlForNonMarketingPath(window.location);
  if (appRedirectUrl) {
    window.location.replace(appRedirectUrl);
    return null;
  }

  return <MainAppRouter />;
}

function AppRouter() {
  return (
    <>
      <JungleColorThemeSync />
      <AppLanguageSync />
      <AppLocaleSync />
      <React.Suspense fallback={<LoadingViewComponent />}>
        <AppRouterContent />
      </React.Suspense>
    </>
  );
}

export default React.memo(AppRouter);
